import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import AdminShell from "@/components/AdminShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
    paid: "border-emerald-400/40 text-emerald-400",
    sent: "border-ember/40 text-ember",
    overdue: "border-red-400/40 text-red-400",
    cancelled: "border-white/10 text-bone-500",
    draft: "border-white/10 text-bone-500",
};

export default function AdminInvoices() {
    const { user, token, loading } = useAuth();
    const [items, setItems] = useState([]);
    const [clients, setClients] = useState({});
    const [busy, setBusy] = useState(true);

    const headers = useMemo(
        () => ({ Authorization: `Bearer ${token}` }),
        [token],
    );

    const refresh = () => {
        setBusy(true);
        return Promise.all([
            axios.get(`${API}/admin/invoices`, { headers }),
            axios.get(`${API}/admin/clients`, { headers }),
        ])
            .then(([i, c]) => {
                setItems(i.data);
                const map = {};
                c.data.forEach((cl) => (map[cl.id] = cl));
                setClients(map);
            })
            .finally(() => setBusy(false));
    };

    useEffect(() => {
        if (token) refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    if (loading) return null;
    if (!user) return <Navigate to="/admin/login" replace />;

    const onMarkPaid = async (invId) => {
        if (!window.confirm("Mark this invoice as paid?")) return;
        try {
            await axios.post(
                `${API}/admin/invoices/${invId}/mark-paid`,
                { paid_method: "bank transfer" },
                { headers },
            );
            toast.success("Marked paid · receipt emailed to client");
            refresh();
        } catch {
            toast.error("Could not mark as paid");
        }
    };

    const onDownloadPdf = async (inv) => {
        try {
            const res = await axios.get(
                `${API}/admin/invoices/${inv.id}/pdf`,
                { headers, responseType: "blob" },
            );
            const url = URL.createObjectURL(
                new Blob([res.data], { type: "application/pdf" }),
            );
            const a = document.createElement("a");
            a.href = url;
            a.download = `${inv.number}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("Could not download PDF");
        }
    };

    const totalPaid = items
        .filter((i) => i.status === "paid")
        .reduce((s, i) => s + i.amount, 0);
    const totalOutstanding = items
        .filter((i) => i.status === "sent")
        .reduce((s, i) => s + i.amount, 0);

    return (
        <AdminShell>
            <div className="mb-8 flex items-end justify-between gap-6 flex-wrap">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-ember mb-2">
                        Billing
                    </div>
                    <h1 className="font-display text-bone text-4xl tracking-tightest leading-[1]">
                        Invoices
                    </h1>
                </div>
                <div className="flex gap-6 text-sm">
                    <Stat label="Outstanding" value={totalOutstanding} accent />
                    <Stat label="Paid" value={totalPaid} />
                </div>
            </div>

            {busy ? (
                <Loader2 className="w-5 h-5 animate-spin text-ember" />
            ) : items.length === 0 ? (
                <div className="border border-white/10 bg-ink-800/40 p-12 text-center text-bone-300">
                    No invoices yet. Issue one from a client's profile.
                </div>
            ) : (
                <ul className="border-t border-white/10">
                    {items.map((inv) => (
                        <li
                            key={inv.id}
                            data-testid={`admin-invoice-row-${inv.id}`}
                            className="border-b border-white/10 grid grid-cols-12 gap-3 items-center py-4 px-2"
                        >
                            <Link
                                to={`/admin/clients/${inv.client_id}`}
                                className="col-span-9 grid grid-cols-9 gap-3 items-center hover:opacity-80"
                            >
                                <span className="col-span-2 text-xs uppercase tracking-[0.18em] text-bone-500">
                                    {inv.number}
                                </span>
                                <span className="col-span-3 font-display text-bone text-base tracking-tight truncate">
                                    {inv.title}
                                </span>
                                <span className="col-span-2 text-bone-300 text-sm truncate">
                                    {clients[inv.client_id]?.name || "—"}
                                </span>
                                <span className="col-span-1 text-bone">
                                    {inv.amount.toFixed(2)}{" "}
                                    {inv.currency.toUpperCase()}
                                </span>
                                <span
                                    className={`col-span-1 justify-self-end text-[10px] uppercase tracking-[0.2em] border px-2 py-1 ${STATUS[inv.status] || ""}`}
                                >
                                    {inv.status}
                                </span>
                            </Link>
                            <div className="col-span-3 justify-self-end flex items-center gap-2">
                                <button
                                    onClick={() => onDownloadPdf(inv)}
                                    data-testid={`pdf-${inv.id}`}
                                    className="inline-flex items-center gap-1.5 h-9 px-3 border border-white/15 hover:border-ember hover:text-ember text-[10px] uppercase tracking-[0.2em] transition-colors"
                                >
                                    <Download className="w-3.5 h-3.5" />
                                    PDF
                                </button>
                                {(inv.status === "sent" || inv.status === "overdue") && (
                                    <button
                                        onClick={() => onMarkPaid(inv.id)}
                                        data-testid={`markpaid-${inv.id}`}
                                        className="btn-ember inline-flex items-center gap-1.5 h-9 px-3 text-[10px] uppercase tracking-[0.2em]"
                                    >
                                        <CheckCircle2 className="w-3.5 h-3.5" />
                                        Mark paid
                                    </button>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </AdminShell>
    );
}

function Stat({ label, value, accent }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-bone-500">
                {label}
            </div>
            <div
                className={`font-display text-2xl tracking-tight ${accent ? "text-ember" : "text-bone"}`}
            >
                ${value.toFixed(2)}
            </div>
        </div>
    );
}
